import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import Swal from 'sweetalert2';

const Container = styled.div`
  padding: 16px;
  height: calc(100vh - 64px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  margin-bottom: 20px;
  text-align: center;
  flex-shrink: 0;
`;

const Title = styled.h1`
  color: #2d3748;
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 8px 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  color: #718096;
  font-size: 14px;
  margin: 0;
`;

const FormContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-right: 8px;
`;

const FormCard = styled.form`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 8px 16px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0 12px 0;

  &:first-child {
    margin-top: 0;
  }
`;

const SectionIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: linear-gradient(135deg, ${props => props.color || '#667eea'} 0%, ${props => props.colorEnd || '#764ba2'} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  color: white;
  font-size: 14px;
`;

const SectionTitle = styled.h3`
 color: #2d3748;
 font-size: 16px;
 font-weight: 600;
 margin: 0;
`;

const GridLayout = styled.div`
 display: grid;
 grid-template-columns: 1fr 1fr;
 gap: 16px;
 margin-bottom: 20px;

 @media (max-width: 768px) {
   grid-template-columns: 1fr;
 }
`;

const FormGroup = styled.div`
 margin-bottom: 16px;

 ${props => props.fullWidth && `
   grid-column: 1 / -1;
 `}
`;

const Label = styled.label`
 display: flex;
 align-items: center;
 margin-bottom: 6px;
 font-weight: 600;
 color: #2d3748;
 font-size: 13px;
`;

const RequiredAsterisk = styled.span`
 color: #e53e3e;
 margin-left: 4px;
`;

const OptionalText = styled.span`
 color: #718096;
 margin-left: 4px;
 font-weight: 400;
 font-style: italic;
`;

const ReadOnlyText = styled.span`
 color: #e53e3e;
 margin-left: 4px;
 font-weight: 500;
 font-size: 12px;
`;

const Input = styled.input`
 width: 100%;
 padding: 12px 16px;
 border: 2px solid #e2e8f0;
 border-radius: 8px;
 font-size: 14px;
 transition: all 0.3s ease;
 background: #f7fafc;
 box-sizing: border-box;

 &:focus {
   outline: none;
   border-color: #667eea;
   background: white;
   box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
 }

 &:disabled {
   background-color: #f1f5f9 !important;
   color: #64748b !important;
   cursor: not-allowed !important;
   border-color: #cbd5e0 !important;
   font-weight: 600;
   text-align: center;
 }
`;

const TextArea = styled.textarea`
 width: 100%;
 padding: 12px 16px;
 border: 2px solid #e2e8f0;
 border-radius: 8px;
 font-size: 14px;
 transition: all 0.3s ease;
 background: #f7fafc;
 resize: vertical;
 min-height: 80px;
 font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
 box-sizing: border-box;

 &:focus {
   outline: none;
   border-color: #667eea;
   background: white;
   box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
 }
`;

const HelpText = styled.p`
 font-size: 11px;
 color: #718096;
 margin-top: 4px;
 font-style: italic;
 line-height: 1.3;
`;

const WarningBox = styled.div`
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  color: #856404;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 12px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const ButtonGroup = styled.div`
 display: flex;
 gap: 12px;
 margin-top: 24px;
 padding-top: 20px;
 border-top: 2px solid #e2e8f0;
 justify-content: center;
 flex-shrink: 0;
`;

const Button = styled.button`
 padding: 12px 24px;
 border-radius: 8px;
 font-size: 14px;
 font-weight: 600;
 cursor: pointer;
 transition: all 0.3s ease;
 min-width: 120px;

 ${props => props.variant === 'primary' ? `
   background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
   color: white;
   border: none;
   box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);

   &:hover {
     transform: translateY(-2px);
     box-shadow: 0 6px 12px rgba(102, 126, 234, 0.4);
   }

   &:disabled {
     background: #a0aec0;
     cursor: not-allowed;
     transform: none;
     box-shadow: none;
   }
 ` : `
   background: #f7fafc;
   color: #4a5568;
   border: 2px solid #e2e8f0;

   &:hover {
     background: #edf2f7;
     border-color: #cbd5e0;
   }
 `}
`;

const BrandRegister = () => {
 const navigate = useNavigate();
 const { id } = useParams();
 const isEdit = Boolean(id);

 const [formData, setFormData] = useState({
   brandCode: '',
   brandName: '',
   username: '',
   apiPassword: '',
   senderId: '',
   whatsappToken: '',
   email: '',
   password: ''
 });

 const [loading, setLoading] = useState(false);
 const [isSubmitting, setIsSubmitting] = useState(false);

 const API_URL2 = import.meta.env.VITE_API_URL2;

 const fetchBrandData = useCallback(async () => {
   if (!id) return;

   try {
     setLoading(true);
     const token = localStorage.getItem('adminToken');

     // Use the full route to get all brand data including sensitive fields
     const response = await fetch(API_URL2 + `/api/admin/brands/${id}/full`, {
       method: 'GET',
       headers: {
         'Authorization': `Bearer ${token}`,
         'Content-Type': 'application/json'
       }
     });

     if (!response.ok) {
       throw new Error('Failed to fetch brand data');
     }

     const data = await response.json();

     if (data.success && data.data) {
       setFormData({
         brandCode: data.data.brandCode || '',
         brandName: data.data.brandName || '',
         username: data.data.username || '',
         apiPassword: '', 
         senderId: data.data.senderId || '',
         whatsappToken: data.data.whatsappToken || '', 
         email: data.data.email || '',
         password: '' 
       });
     }
   } catch (error) {
     Swal.fire({
       title: 'Error!',
       text: 'Failed to fetch brand data',
       icon: 'error',
       confirmButtonColor: '#e53e3e'
     });
   } finally {
     setLoading(false);
   }
 }, [id]);

 useEffect(() => {
   if (isEdit && id) {
     fetchBrandData();
   }
 }, [isEdit, id, fetchBrandData]);

 const handleChange = useCallback((e) => {
   const { name, value } = e.target;

   // Prevent brand code changes in edit mode
   if (name === 'brandCode' && isEdit) {
     Swal.fire({
       title: 'Action Not Allowed',
       text: 'Brand code cannot be changed to maintain WhatsApp connections and data integrity',
       icon: 'warning',
       confirmButtonColor: '#667eea'
     });
     return;
   }

   // Auto-uppercase brand code
   if (name === 'brandCode') {
     setFormData(prev => ({
       ...prev,
       [name]: value.toUpperCase()
     }));
   } else {
     setFormData(prev => ({
       ...prev,
       [name]: value
     }));
   }
 }, [isEdit]);

 const validateForm = useCallback(() => {
   // Required fields check
   const requiredFields = ['brandCode', 'brandName', 'username', 'senderId', 'email'];

   for (let field of requiredFields) {
     if (!formData[field] || formData[field].trim() === '') {
       Swal.fire({
         title: 'Validation Error',
         text: `${field.charAt(0).toUpperCase() + field.slice(1)} is required`,
         icon: 'warning',
         confirmButtonColor: '#667eea'
       });
       return false;
     }
   }

   // Password requirements for new brands only
   if (!isEdit) {
     if (!formData.apiPassword || formData.apiPassword.trim() === '') {
       Swal.fire({
         title: 'Validation Error',
         text: 'API password is required for new brands',
         icon: 'warning',
         confirmButtonColor: '#667eea'
       });
       return false;
     }

     if (!formData.password || formData.password.trim() === '') {
       Swal.fire({
         title: 'Validation Error',
         text: 'Dashboard password is required for new brands',
         icon: 'warning',
         confirmButtonColor: '#667eea'
       });
       return false;
     }

     if (!formData.whatsappToken || formData.whatsappToken.trim() === '') {
       Swal.fire({
         title: 'Validation Error',
         text: 'WhatsApp Access Token is required for new brands',
         icon: 'warning',
         confirmButtonColor: '#667eea'
       });
       return false;
     }
   }

   // Brand code format validation
   if (!/^[A-Z0-9]{3,10}$/.test(formData.brandCode)) {
     Swal.fire({
       title: 'Validation Error',
       text: 'Brand Code must be 3-10 uppercase letters/numbers only',
       icon: 'warning',
       confirmButtonColor: '#667eea'
     });
     return false;
   }

   // Email format validation
   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
   if (!emailRegex.test(formData.email)) {
     Swal.fire({
       title: 'Validation Error',
       text: 'Please enter a valid email address',
       icon: 'warning',
       confirmButtonColor: '#667eea'
     });
     return false;
   }

   // Sender ID validation
   if (!/^\d{12,20}$/.test(formData.senderId)) {
     Swal.fire({
       title: 'Validation Error',
       text: 'Sender ID must be a 12-20 digit number from WhatsApp Business API',
       icon: 'warning',
       confirmButtonColor: '#667eea'
     });
     return false;
   }

   // Password length validation (only if provided)
   if (formData.password && formData.password.length < 6) {
     Swal.fire({
       title: 'Validation Error',
       text: 'Password must be at least 6 characters long',
       icon: 'warning',
       confirmButtonColor: '#667eea'
     });
     return false;
   }

   return true;
 }, [formData, isEdit]);

 const handleSubmit = useCallback(async (e) => {
  e.preventDefault();
  e.stopPropagation();

  if (isSubmitting || loading) return;
  if (!validateForm()) return;

  try {
    setIsSubmitting(true);
    setLoading(true);

    const submitData = {
      brandCode: formData.brandCode.trim(),
      brandName: formData.brandName.trim(),
      username: formData.username.trim(),
      senderId: formData.senderId.trim(),
      email: formData.email.trim().toLowerCase()
    };

    if (formData.password && formData.password.trim()) {
      submitData.password = formData.password.trim();
    }
    if (formData.apiPassword && formData.apiPassword.trim()) {
      submitData.apiPassword = formData.apiPassword.trim();
    }
    if (formData.whatsappToken && formData.whatsappToken.trim()) {
      submitData.whatsappToken = formData.whatsappToken.trim();
    }

    const token = localStorage.getItem('adminToken');
    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }

    let response;
    if (isEdit) {
      response = await fetch(API_URL2 + `/api/admin/brands/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });
    } else {
      response = await fetch(API_URL2 + '/api/admin/brands', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();

    if (data.success) {

      const result = await Swal.fire({
        title: 'Success!',
        text: isEdit ? 'Brand updated successfully!' : 'Brand registered successfully! They can now login to their dashboard.',
        icon: 'success',
        showCancelButton: true,
        confirmButtonColor: '#667eea',
        cancelButtonColor: '#718096',
        confirmButtonText: 'Go to Brands List',
        cancelButtonText: isEdit ? 'Stay Here' : 'Add Another Brand',
        allowOutsideClick: true,
        allowEscapeKey: true

      });

      if (result.isConfirmed) {
        navigate('/admin-dhobicart/brands', { replace: true });
      } else if (result.isDismissed && result.dismiss === Swal.DismissReason.cancel && !isEdit) {
        // Reset form for new brand registration only if user chose "Add Another Brand"
        setFormData({
          brandCode: '',
          brandName: '',
          username: '',
          apiPassword: '',
          senderId: '',
          whatsappToken: '',
          email: '',
          password: ''
        });
      }

    } else {
      throw new Error(data.message || `Failed to ${isEdit ? 'update' : 'register'} brand`);
    }

  } catch (error) {
    let errorMessage = `Failed to ${isEdit ? 'update' : 'register'} brand`;
    if (error.message) {
      errorMessage = error.message;
    }

    Swal.fire({
      title: 'Error!',
      text: errorMessage,
      icon: 'error',
      confirmButtonColor: '#e53e3e',
      allowOutsideClick: true,
      allowEscapeKey: true
    });
  } finally {
    setLoading(false);
    setIsSubmitting(false);
  }
}, [formData, validateForm, isEdit, id, isSubmitting, loading, navigate]);

 const handleCancel = useCallback((e) => {
   if (e) {
     e.preventDefault();
     e.stopPropagation();
   }
   navigate('/admin-dhobicart/brands', { replace: true });
 }, [navigate]);

 return (
   <Container>
     <Header>
       <Title>{isEdit ? 'Edit Brand' : 'Register New Brand'}</Title>
       <Subtitle>
         {isEdit ? 'Update brand information and settings' : 'Create a new brand for WhatsApp integration'}
       </Subtitle>
     </Header>

     <FormContainer>
       <FormCard onSubmit={handleSubmit}>
         {/* Warning box for edit mode */}
         {isEdit && (
           <WarningBox>
             <span>⚠️</span>
             <div>
               <strong>Important:</strong> Brand Code cannot be changed to maintain WhatsApp connections and data integrity.
               Changing the brand code would break existing conversations and contacts.
             </div>
           </WarningBox>
         )}

         {/* Brand Information Section */}
         <SectionHeader>
           <SectionIcon color="#667eea" colorEnd="#764ba2">🏢</SectionIcon>
           <SectionTitle>Brand Information</SectionTitle>
         </SectionHeader>

         <GridLayout>
           <FormGroup>
             <Label htmlFor="brandCode">
               Brand Code<RequiredAsterisk>*</RequiredAsterisk>
               {isEdit && <ReadOnlyText>(Read Only)</ReadOnlyText>}
             </Label>
             <Input
               type="text"
               id="brandCode"
               name="brandCode"
               value={formData.brandCode}
               onChange={handleChange}
               placeholder="e.g., DCOIT"
               maxLength="10"
               required
               autoComplete="off"
               disabled={isEdit}
               readOnly={isEdit}
               onClick={isEdit ? (e) => {
                 e.preventDefault();
                 Swal.fire({
                   title: 'Field Locked',
                   text: 'Brand code cannot be modified to maintain WhatsApp connections',
                   icon: 'info',
                   confirmButtonColor: '#667eea'
                 });
               } : undefined}
             />
             <HelpText>
               {isEdit ? '🔒 Brand code is permanently locked to prevent breaking WhatsApp connections' : '3-10 characters, uppercase only. Cannot change after creation.'}
             </HelpText>
           </FormGroup>

           <FormGroup>
             <Label htmlFor="brandName">
               Brand Display Name<RequiredAsterisk>*</RequiredAsterisk>
             </Label>
             <Input
               type="text"
               id="brandName"
               name="brandName"
               value={formData.brandName}
               placeholder="e.g., Dry Cleaners"
               onChange={handleChange}
               maxLength="50"
               required
               autoComplete="off"
             />
             <HelpText>Displayed in dashboard and WhatsApp messages.</HelpText>
           </FormGroup>
         </GridLayout>

         {/* API Configuration Section */}
         <SectionHeader>
           <SectionIcon color="#8b5cf6" colorEnd="#7c3aed">⚙️</SectionIcon>
           <SectionTitle>API Configuration</SectionTitle>
         </SectionHeader>

         <GridLayout>
           <FormGroup>
             <Label htmlFor="username">
               API Username<RequiredAsterisk>*</RequiredAsterisk>
             </Label>
             <Input
               type="text"
               id="username"
               name="username"
               value={formData.username}
               onChange={handleChange}
               placeholder="e.g., DCOITIQB"
               maxLength="20"
               required
               autoComplete="off"
             />
             <HelpText>Username for DhobiCart API authentication.</HelpText>
           </FormGroup>

           <FormGroup>
             <Label htmlFor="apiPassword">
               API Password
               {!isEdit && <RequiredAsterisk>*</RequiredAsterisk>}
               {isEdit && <OptionalText>(Optional)</OptionalText>}
             </Label>
             <Input
               type="password"
               id="apiPassword"
               name="apiPassword"
               value={formData.apiPassword}
               onChange={handleChange}
               placeholder={isEdit ? "Leave empty to keep current" : "e.g., 567"}
               required={!isEdit}
               autoComplete="new-password"
             />
             <HelpText>
               {isEdit ? 'Leave blank to keep current password.' : 'Password for DhobiCart API.'}
             </HelpText>
           </FormGroup>
         </GridLayout>

         {/* WhatsApp Configuration Section */}
         <SectionHeader>
           <SectionIcon color="#25d366" colorEnd="#128c7e">📱</SectionIcon>
           <SectionTitle>WhatsApp Configuration</SectionTitle>
         </SectionHeader>

         <FormGroup>
           <Label htmlFor="senderId">
             Phone Number ID<RequiredAsterisk>*</RequiredAsterisk>
           </Label>
           <Input
             type="text"
             id="senderId"
             name="senderId"
             value={formData.senderId}
             onChange={handleChange}
             placeholder="e.g., 457480074125192"
             pattern="[0-9]{12,20}"
             required
             autoComplete="off"
           />
           <HelpText>WhatsApp Business API Phone Number ID (12-20 digits).</HelpText>
         </FormGroup>

         <FormGroup fullWidth>
           <Label htmlFor="whatsappToken">
             WhatsApp Access Token
             {!isEdit && <RequiredAsterisk>*</RequiredAsterisk>}
             {isEdit && <OptionalText>(Optional)</OptionalText>}
           </Label>
           <TextArea
             id="whatsappToken"
             name="whatsappToken"
             value={formData.whatsappToken}
             onChange={handleChange}
             placeholder={isEdit ? "Leave empty to keep current token..." : "Enter your WhatsApp Business API access token here..."}
             rows="3"
             required={!isEdit}
             autoComplete="off"
           />
           <HelpText>
             {isEdit ? 'Leave blank to keep current token. Update only if token has changed.' : 'Permanent access token from Meta Business. Keep secure!'}
           </HelpText>
         </FormGroup>

         {/* Dashboard Login Credentials Section */}
         <SectionHeader>
           <SectionIcon color="#ed8936" colorEnd="#dd6b20">🔐</SectionIcon>
           <SectionTitle>Dashboard Credentials</SectionTitle>
         </SectionHeader>

         <GridLayout>
           <FormGroup>
             <Label htmlFor="email">
               Email Address<RequiredAsterisk>*</RequiredAsterisk>
             </Label>
             <Input
               type="email"
               id="email"
               name="email"
               value={formData.email}
               onChange={handleChange}
               placeholder="brand@example.com"
               required
               autoComplete="email"
             />
             <HelpText>Email for dashboard login.</HelpText>
           </FormGroup>

           <FormGroup>
             <Label htmlFor="password">
               Dashboard Password
               {!isEdit && <RequiredAsterisk>*</RequiredAsterisk>}
               {isEdit && <OptionalText>(Optional)</OptionalText>}
             </Label>
             <Input
               type="password"
               id="password"
               name="password"
               value={formData.password}
               onChange={handleChange}
               placeholder={isEdit ? "Leave empty to keep current" : "Enter secure password"}
               minLength="6"
               required={!isEdit}
               autoComplete="new-password"
             />
             <HelpText>
               {isEdit ? 'Leave blank to keep current password.' : 'Minimum 6 characters.'}
             </HelpText>
           </FormGroup>
         </GridLayout>

         <ButtonGroup>
           <Button
             type="submit"
             variant="primary"
             disabled={loading || isSubmitting}
           >
             {loading || isSubmitting ? 'Processing...' : (isEdit ? 'Update Brand' : 'Register Brand')}
           </Button>
           <Button type="button" onClick={handleCancel}>
             Cancel
           </Button>
         </ButtonGroup>
       </FormCard>
     </FormContainer>
   </Container>
 );
};

export default BrandRegister;
